import { Feedback } from '@libs/src/feedback/feedback';
import { Account } from '@libs/src/models/account.model';
import { Conversation } from '@libs/src/models/conversation.interfaces';
import { Group } from '@libs/src/models/group.model';
import { Match } from '@libs/src/models/match.interfaces';
import { PathwayMembershipUnpopulated } from '@libs/src/models/pathway-membership.interface';
import { PathwayPopulated } from '@libs/src/models/pathway.interface';
import {
  ProgramMembership,
  ProgramMembershipWithoutPopulatedProgram,
} from '@libs/src/models/program-membership.model';
import { ProgramWithoutPopulatedSurvey } from '@libs/src/models/program.model';
import { SurveyResponseWithPopulatedSurvey } from '@libs/src/program-track-indicator/program-track-indicator.helper';
import {
  Opportunity,
  OpportunitySubmission,
} from '@main-client/src/app/opportunity/opportunity.model';

export interface Experience {
  account?: Account;
  attendees?: Account[];
  doc_type: 'event' | 'introduction' | 'program';
  end_date?: Date;
  event?: Opportunity;
  feedback?: Feedback[];
  group?: Group;
  match?: Match;
  program_membership?: ProgramMembership;
  start_date?: Date;
  submission?: OpportunitySubmission;
  supporting_document?: string;
  survey_responses?: SurveyResponseWithPopulatedSurvey[];
}

export interface AccountExperience {
  account?: Account;
  experiences?: Experience[];
  group?: Group;
}

export const enum ExperienceStatus {
  'behind' = 'behind',
  'checkin' = 'checkin',
  'completed' = 'completed',
  'current' = 'current',
  'happeningNow' = 'happeningNow',
  'lagging' = 'lagging',
  'onTrack' = 'onTrack',
  'pending' = 'pending',
  'skipped' = 'skipped',
  'upcoming' = 'upcoming',
  'waitingForPairing' = 'waitingForPairing',
}

export const enum EventTimeState {
  'future' = 'future',
  'now' = 'now',
  'past' = 'past',
}

export const GroupExperienceType = {
  conversation: 'conversation',
  event: 'event',
  introduction: 'introduction',
  pathway: 'pathway',
  program: 'program',
} as const;

interface BaseOpportunityGroupExperience {
  event?: Pick<
    Opportunity,
    | '_id'
    | 'event_end_date'
    | 'event_start_date'
    | 'featured_accounts'
    | 'opportunity_type'
    | 'owner_group'
    | 'slug'
    | 'title'
  >;
  pathway?: Pick<PathwayPopulated, '_id' | 'name'>;
  type: typeof GroupExperienceType.event;
}

export interface AttendedOpportunityGroupExperience
  extends BaseOpportunityGroupExperience {
  feedback?: Feedback[];
  opportunity_submission?: OpportunitySubmission;
}

export interface HostedOpportunityGroupExperience
  extends BaseOpportunityGroupExperience {
  average_rating?: number;
  confirmed_submissions_count?: number;
  waitlisted_submissions_count?: number;
}

export type OpportunityGroupExperience =
  | AttendedOpportunityGroupExperience
  | HostedOpportunityGroupExperience;

export interface IntroductionGroupExperience {
  feedback?: Feedback[];
  match?: Match;
  type: typeof GroupExperienceType.introduction;
}

export interface ProgramGroupExperience {
  matches?: Match[];
  pathway?: Pick<PathwayPopulated, '_id' | 'name'>;
  program?: Pick<
    ProgramWithoutPopulatedSurvey,
    | '_id'
    | 'end_date'
    | 'features_enabled'
    | 'name'
    | 'parent_program'
    | 'phases'
    | 'program_config'
    | 'program_type'
    | 'start_date'
  >;
  program_membership?: ProgramMembershipWithoutPopulatedProgram & {
    created_at: Date;
  };
  survey_responses?: SurveyResponseWithPopulatedSurvey[];
  type: typeof GroupExperienceType.program;
}

export interface PathwayGroupExperience {
  pathway?: Pick<PathwayPopulated, '_id' | 'name'>;
  pathway_membership?: Pick<
    PathwayMembershipUnpopulated,
    '_id' | 'account' | 'created_at' | 'status'
  >;
  type: typeof GroupExperienceType.pathway;
}

export interface ConversationGroupExperience {
  conversation: Pick<Conversation, 'created_at' | 'participants'>;
  type: typeof GroupExperienceType.conversation;
}

export type GroupExperience =
  | ConversationGroupExperience
  | IntroductionGroupExperience
  | OpportunityGroupExperience
  | PathwayGroupExperience
  | ProgramGroupExperience;
